/**
 * Hooks and utilities for working with the Users API.
 */

import React from 'react';
import {
  useProfile,
  useActivityCount,
  useContact as useTokenizedContact,
  useIsLoggedIn,
  usePrimaryLockbox,
  CURRENCY_BSV,
} from '@tokenized/sdk-react-private';
import { useHistory, useParams } from 'react-router-dom';
import { pendingFilter } from '#components/Pages/Overview/ActivityTable/ActivityTable';
import userPeristentState, {
  usePersistentGlobalState,
} from '#modules/utils/usePersistentState';
import useLiquidNobleApi, {
  useActiveEntityIds,
} from '#modules/utils/useLiquidNobleApi';
import {
  isLiquidNobleLoggedIn,
  saveAuthToken,
  saveSessionUsername,
  clearSessionMetadata,
  getSessionUnlockingKey,
} from '#modules/utils/jwt';
import { LOCKBOX_TYPE_TRANSACTION } from '#components/Pages/Lockboxes/LockboxesTable';
import { isValidHandle } from '#modules/utils/validatorsLocalized';
import { EntityContext } from '#modules/context/EntityContext';
import { LockboxContext } from '#modules/context/LockboxContext';
import { InboxContext } from '#modules/context/InboxContext';
import { errorResponseNotify } from '#modules/utils/errors';
import {
  KYC_STATUS_NONE,
  KYC_STATUS_EXPRESS_APPROVED,
  KYC_STATUS_RESTRICTED_APPROVED,
  KYC_STATUS_OK,
  KYC_STATUS_STARTED,
  KYC_STATUS_UPGRADE_STARTED,
  ONBOARDING_SUGGESTION_ADD_BANK,
} from '#modules/utils/kyc';
import { useRedirect, useKycRedirect } from '#modules/hooks/navigation';
import usePasskey from '#modules/hooks/usePasskey';

const loadingEntities = {};
// Stateless replica of setLockboxLoading to address the issue of lockbox loading being called multiple times simultaneously on page refresh
const loadingLockboxes = {};
const loadingContractDetails = {};
let loadingProfiles = false;
let contractsLoading = false;

export const MFA_TYPE_NONE = 0;
export const MFA_TYPE_EMAIL = 1;
export const MFA_TYPE_APP = 2;
export const MFA_TYPE_TOKENIZED = 3;

export const VALID_HANDLE_DOMAINS = ['lqn.app', 'tkz.id'];

export function useTkzEntity() {
  return null;
}

function toCamelCase(input) {
  return input
    .replace(/./, (text) => text.toLowerCase())
    .replaceAll(/_(.)/g, (_, initial) => initial.toUpperCase());
}

export function normalizeValue(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }
  if (typeof value === 'object' && value !== null) {
    const normalized = {};
    for (const [key, val] of Object.entries(value)) {
      normalized[toCamelCase(key)] = normalizeValue(val);
    }
    return normalized;
  }
  return value;
}

export const useInboxMessagesForEntity = (entityId) => {
  const liquidNobleApi = useLiquidNobleApi();

  const { inboxMessages, setInboxMessages } = React.useContext(InboxContext);

  const refetch = React.useCallback(async () => {
    if (!entityId || loadingEntities[entityId]) {
      return;
    }

    loadingEntities[entityId] = true;
    try {
      const response = await liquidNobleApi.get('/user/inbox/', {
        params: {
          entity_id: entityId,
        },
      });

      const { data } = response;
      loadingEntities[entityId] = false;
      setInboxMessages({
        ...inboxMessages,
        [entityId]: data?.messages ?? [],
      });
    } catch (err) {
      console.error('API error: ', err);
      loadingEntities[entityId] = false;
      setInboxMessages({
        ...inboxMessages,
        [entityId]: [],
      });
    }
  }, [liquidNobleApi, setInboxMessages, entityId, inboxMessages]);
  React.useEffect(() => {
    async function loadInboxMessages() {
      if (
        !entityId ||
        (inboxMessages && inboxMessages[entityId] !== undefined)
      ) {
        return;
      }
      refetch();
    }
    loadInboxMessages();
  }, [inboxMessages, setInboxMessages, liquidNobleApi, refetch, entityId]);
  return [
    inboxMessages?.[entityId],
    refetch,
    loadingEntities?.[entityId] ?? false,
  ];
};

export const useActiveEntityId = () => {
  const { activeEntityId } = React.useContext(EntityContext);
  return activeEntityId;
};

export const useInboxMessages = () => {
  const { activeEntityId } = React.useContext(EntityContext);
  return useInboxMessagesForEntity(activeEntityId);
};

export const useUnreadMessages = () => {
  const [inboxMessages] = useInboxMessages();
  const unreadMessages = React.useMemo(() => {
    return inboxMessages?.filter((message) => message.is_read === false) ?? [];
  }, [inboxMessages]);
  return unreadMessages;
};

export const useImportantMessages = () => {
  const [inboxMessages] = useInboxMessages();
  const importantMessages = React.useMemo(() => {
    return (
      inboxMessages?.filter((message) => message.is_important === true) ?? []
    );
  }, [inboxMessages]);
  return importantMessages;
};

export const useUnimportantMessages = () => {
  const [inboxMessages] = useInboxMessages();
  const unimportantMessages = React.useMemo(() => {
    return (
      inboxMessages?.filter((message) => message.is_important === false) ?? []
    );
  }, [inboxMessages]);
  return unimportantMessages;
};

export const useUnreadMessagesCount = () => useUnreadMessages()?.length ?? 0;

export const useImportantMessagesCount = () =>
  useImportantMessages()?.length ?? 0;

export const useAnyActivityCount = ({ profileId }) => {
  const profile = useProfile(profileId);
  const messages = useInboxMessagesForEntity(profile?.entityId);
  const unreadMessages = messages?.[0]?.filter(
    (message) => message.is_read === false,
  ).length;

  const activityCount =
    useActivityCount({
      profileId,
      overviewFilter: pendingFilter,
      filters: {
        includeSuccessfulEvents: false,
        includeIncompleteEvents: false,
        includeFailedEvents: false,
        includeExpiredEvents: false,
        includeEventsRequiringAction: true,
        includeEventsPendingOthers: false,
      },
    }) ||
    unreadMessages ||
    0;

  return activityCount;
};

export const useLiquidNobleProfile = (entityId, withBankDetails) => {
  const [profile, setProfile] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();

  const loadProfile = React.useCallback(async () => {
    if (!entityId) {
      return;
    }

    const response = await liquidNobleApi.get('/user/profile/', {
      params: {
        entity_id: entityId,
        with_bank_details: withBankDetails,
      },
    });

    const { data } = response;
    if (data) {
      setProfile(data);
    }
  }, [liquidNobleApi, entityId, withBankDetails]);

  React.useEffect(() => {
    loadProfile();
  }, [loadProfile]);
  return [profile, loadProfile];
};

const mandatoryTransferLogFields = ['transaction_id', 'activity_id', 'handle'];

export const useRecordInstrumentTransfer = () => {
  const liquidNobleApi = useLiquidNobleApi();

  const recordInstrumentTransfer = React.useCallback(
    async (transferData) => {
      const missingFields = mandatoryTransferLogFields.filter(
        (field) => !transferData[field],
      );
      if (missingFields.length) {
        console.log(
          `Skipping recordInstrumentTransfer: Missing mandatory fields: ${missingFields.join(
            ', ',
          )}`,
        );
        return;
      }
      try {
        console.log('useRecordInstrumentTransfer: ', transferData);
        const response = await liquidNobleApi.post('/user/transfer/log/', {
          ...transferData,
        });
        return response;
      } catch (err) {
        console.log('API error: ', err);
        return null;
      }
    },
    [liquidNobleApi],
  );

  return recordInstrumentTransfer;
};

export const useUpdateEntityInfo = () => {
  const liquidNobleApi = useLiquidNobleApi();

  const updateEntityInfo = React.useCallback(
    async (entityId, postData) => {
      if (!entityId) {
        console.log('updateEntityInfo failed: No entity ID provided');
        return;
      }
      try {
        const response = await liquidNobleApi.post(
          `/user/${entityId}/entity/info/update/`,
          {
            ...postData,
            entity_id: entityId,
          },
        );
        return response;
      } catch (err) {
        console.log('API error: ', err);
        return null;
      }
    },
    [liquidNobleApi],
  );

  return updateEntityInfo;
};

export const useLiquidNobleEntityInfo = (
  entityId,
  autoFetch = true,
  forceReload = false,
  anySessionType = false,
) => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityInfo, setEntityInfo } = React.useContext(EntityContext);

  const loadEntityInfo = React.useCallback(async () => {
    if (!entityId) {
      return;
    }
    if (!isLiquidNobleLoggedIn() && !anySessionType) {
      return;
    }
    if (entityInfo?.[entityId] && !forceReload) {
      return;
    }

    const response = await liquidNobleApi.get(`/user/${entityId}/entity/info/`);
    const { data } = response;
    if (data?.entity) {
      // Transform snake_case keys to camelCase as expected by Tokenized SDK
      const normalizedData = normalizeValue(data?.entity);
      setEntityInfo((prevEntityInfo) => ({
        ...prevEntityInfo,
        [entityId]: normalizedData,
      }));
    }
  }, [
    liquidNobleApi,
    entityId,
    setEntityInfo,
    entityInfo,
    forceReload,
    anySessionType,
  ]);

  React.useEffect(() => {
    if (autoFetch) {
      loadEntityInfo();
    }
  }, [loadEntityInfo, autoFetch]);
  return [entityInfo?.[entityId], loadEntityInfo];
};

// export const useLiquidNobleProfileInfo = (profileId) => {
//   const liquidNobleApi = useLiquidNobleApi();
//   const [profileInfo, setProfileInfo] = React.useState(null);
//   React.useEffect(() => {
//     async function loadProfileInfo() {
//       if (!profileId) {
//         return;
//       }
//       if (!isLiquidNobleLoggedIn()) {
//         return;
//       }
//       const response = await liquidNobleApi.get(
//         `/user/${profileId}/profile/info/`,
//       );
//       const { data } = response;
//       if (data?.profile) {
//         // Transform snake_case keys to camelCase as expected by Tokenized SDK
//         const normalizedData = normalizeValue(data?.profile);
//         setProfileInfo(normalizedData);
//       }
//     }
//     loadProfileInfo();
//   }, [liquidNobleApi, profileId, setProfileInfo]);
//   return profileInfo;
// };

export const useLiquidNobleActiveProfiles = () => {
  const { activeProfiles, setActiveProfiles } = React.useContext(EntityContext);
  const liquidNobleApi = useLiquidNobleApi();
  const isLoggedIn = useAnyProviderLoggedIn();

  const loadProfileList = React.useCallback(
    async (force = false) => {
      if (
        !isLoggedIn ||
        loadingProfiles ||
        (activeProfiles !== null && !force)
      ) {
        return;
      }
      loadingProfiles = true;
      const response = await liquidNobleApi.get(`/user/profiles/`);
      const { data } = response;
      if (data?.profiles) {
        const needsMigration = !!data?.needs_migration;
        // Transform snake_case keys to camelCase as expected by Tokenized SDK
        const mergedProfiles = data?.profiles.map((profile) => ({
          ...normalizeValue(profile),
          needsMigration,
        }));
        setActiveProfiles(mergedProfiles);
      }
      loadingProfiles = false;
    },
    [liquidNobleApi, activeProfiles, setActiveProfiles, isLoggedIn],
  );

  React.useEffect(() => {
    loadProfileList();
  }, [loadProfileList]);
  return [activeProfiles ?? [], loadProfileList];
};

// eslint-disable-next-line no-unused-vars
export const useContact = (handle, counterparty = null) => {
  // This is a drop-in replacement for the Tokenized SDK useContact hook
  const fetchedContact = useTokenizedContact(
    handle?.includes('@lqn.app') ? undefined : handle,
    counterparty,
  );
  const liquidNobleApi = useLiquidNobleApi();
  const [contact, setContact] = React.useState(null);
  React.useEffect(() => {
    async function loadContact() {
      if (!handle) {
        return;
      }
      if (!isValidHandle(handle)) {
        return;
      }

      // stip off the @ from the handle and everything that follows it
      let handlePrefix = handle.split('@')[0];
      // URLEncode the handle prefix
      handlePrefix = encodeURIComponent(handlePrefix);
      try {
        const response = await liquidNobleApi.get(
          `/user/contact/?handle=${handlePrefix}`,
        );
        const { data } = response;
        if (data?.contact) {
          // Transform snake_case keys to camelCase as expected by Tokenized SDK
          const normalizedData = normalizeValue(data?.contact);
          setContact(normalizedData);
        }
      } catch (err) {
        console.error('/user/contact/ API error: ', err);
      }
    }
    loadContact();
  }, [liquidNobleApi, handle, setContact]);

  const fetchedIsValid = fetchedContact?.isValid;
  return fetchedIsValid || !contact ? fetchedContact : contact;
};

export const useReloadLiquidNobleEntityInfo = (entityId) => {
  const [, loadEntityInfo] = useLiquidNobleEntityInfo(entityId, false, true);
  return loadEntityInfo;
};

export const useAnyProviderEntity = (
  profileId,
  entityId,
  anySessionType = false,
  forceReload = false,
) => {
  const [entity, setEntity] = React.useState(null);
  const [lqnEntity] = useLiquidNobleEntityInfo(
    entityId,
    true,
    forceReload,
    anySessionType,
  );
  React.useEffect(() => {
    async function loadEntity() {
      if (!entityId) {
        return;
      }
      if (lqnEntity) {
        setEntity(lqnEntity);
      }
    }

    loadEntity();

    return () => {
      setEntity(null);
    };
  }, [lqnEntity, entityId]);

  return entity;
};

export const useActiveEntity = (forceReload = false) => {
  const { profileId, entityId } = useActiveEntityIds();
  return useAnyProviderEntity(profileId, entityId, false, forceReload);
};

export const useParseDetailsSyncResponse = () => {
  const redirect = useRedirect();
  const kycRedirect = useKycRedirect();
  const [bankingSuggested] = userPeristentState('bankingSuggested', false);
  const [giftCardCode] = usePersistentGlobalState('redemption_code', null);

  return React.useCallback(
    (
      responseData,
      ignoreKyc = false,
      skipMfaRedirect = false,
      suggestionRedirects = false,
    ) => {
      if (responseData === null || responseData === undefined) {
        return false;
      }
      if (!responseData?.email_verified) {
        redirect('/verify-email');
        return true;
      } else if (responseData?.registration_required) {
        redirect('/entity-registration');
        return true;
      } else if (!responseData?.mfa_type_selected && !skipMfaRedirect) {
        redirect('/mfa-setup');
        return true;
      } else if (giftCardCode) {
        redirect('/redeem', {
          code: giftCardCode,
        });
        return true;
      } else if (
        !ignoreKyc &&
        ![
          KYC_STATUS_NONE,
          KYC_STATUS_STARTED,
          KYC_STATUS_OK,
          KYC_STATUS_EXPRESS_APPROVED,
          KYC_STATUS_RESTRICTED_APPROVED,
          KYC_STATUS_UPGRADE_STARTED,
        ].includes(responseData?.kyc_status)
      ) {
        kycRedirect(responseData.kyc_status);
        return true;
      } else if (
        suggestionRedirects &&
        !bankingSuggested &&
        responseData?.onboarding_suggestion === ONBOARDING_SUGGESTION_ADD_BANK
      ) {
        redirect('/banking');
        return true;
      }
      return false;
    },
    [redirect, kycRedirect, bankingSuggested, giftCardCode],
  );
};

export const useLockboxBalanceRefetch = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);
  const { setLockboxBalances, setLockboxLoading } =
    React.useContext(LockboxContext);

  const refetchLockboxBalance = React.useCallback(
    async (lockboxId, options = {}) => {
      const {
        force = false,
        useEntityId = null,
        onSuccess = null,
        onError = null,
      } = options;

      const entityId = useEntityId || activeEntityId;

      // Validation checks
      if (!lockboxId || !entityId) {
        return false;
      }

      // Prevent duplicate calls unless forced
      if (!force && loadingLockboxes[lockboxId]) {
        return false;
      }

      // Set loading states
      loadingLockboxes[lockboxId] = true;
      setLockboxLoading((prevLoading) => ({
        ...prevLoading,
        [lockboxId]: true,
      }));

      // Determine endpoint based on lockboxId
      const endpoint =
        lockboxId === 'combined'
          ? `/entity/${entityId}/balances/`
          : `/lockbox/${lockboxId}/balances/`;

      try {
        const response = await liquidNobleApi.get(endpoint);
        const { data } = response;

        // Normalize and update the balance data
        const normalizedData = normalizeValue(data?.balances ?? []);
        setLockboxBalances((prevState) => ({
          ...prevState,
          [lockboxId]: normalizedData,
        }));

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(normalizedData);
        }

        return true;
      } catch (err) {
        console.error(
          `useLockboxBalanceRefetch: API error for lockbox ${lockboxId}:`,
          err,
        );

        // Set empty array on error
        setLockboxBalances((prevState) => ({
          ...prevState,
          [lockboxId]: [],
        }));

        // Call error callback if provided
        if (onError) {
          onError(err);
        }

        return false;
      } finally {
        // Clean up loading states
        loadingLockboxes[lockboxId] = false;
        setLockboxLoading((prevLoading) => ({
          ...prevLoading,
          [lockboxId]: false,
        }));
      }
    },
    [liquidNobleApi, activeEntityId, setLockboxBalances, setLockboxLoading],
  );

  return refetchLockboxBalance;
};

export const useRefetchActiveLockboxBalances = () => {
  const { lockboxId } = useParams() || {};
  const { activeLockboxId } = React.useContext(EntityContext);

  const useLockboxId = lockboxId || activeLockboxId;
  const refetchLockboxBalances = useLockboxBalanceRefetch();

  const refetch = React.useCallback(
    async (options = {}) => {
      return refetchLockboxBalances(useLockboxId, options);
    },
    [refetchLockboxBalances, useLockboxId],
  );
  return refetch;
};

export const useLockboxBalances = (lockboxId, autoFetch = true) => {
  const liquidNobleApi = useLiquidNobleApi();
  const { lockboxBalances, lockboxLoading } = React.useContext(LockboxContext);

  const refetch = useLockboxBalanceRefetch();
  React.useEffect(() => {
    async function loadLockboxBalances() {
      if (!autoFetch) {
        return;
      }
      if (
        !lockboxId ||
        (lockboxBalances && lockboxBalances[lockboxId] !== undefined)
      ) {
        return;
      }
      refetch(lockboxId);
    }
    loadLockboxBalances();
  }, [autoFetch, lockboxBalances, liquidNobleApi, refetch, lockboxId]);
  return [lockboxBalances?.[lockboxId], lockboxLoading?.[lockboxId] ?? false];
};

export const useInstrumentContractDetails = (
  instrumentId,
  skipSessionTypeCheck = false,
) => {
  const liquidNobleApi = useLiquidNobleApi();

  const {
    contractDetails,
    setContractDetails,
    contractDetailsLoading,
    setContractDetailsLoading,
  } = React.useContext(LockboxContext);

  const refetch = React.useCallback(
    async (force = false) => {
      if (
        !instrumentId ||
        (!isLiquidNobleLoggedIn() && !skipSessionTypeCheck) ||
        contractDetailsLoading?.[instrumentId] ||
        loadingContractDetails[instrumentId] ||
        instrumentId === CURRENCY_BSV
      ) {
        return;
      }

      if (!force && contractDetails?.[instrumentId] !== undefined) {
        return;
      }

      loadingContractDetails[instrumentId] = true;
      setContractDetailsLoading((prevLoading) => ({
        ...prevLoading,
        [instrumentId]: true,
      }));

      try {
        const response = await liquidNobleApi.get(
          `/contracts/${instrumentId}/instrument/`,
        );

        const { data } = response;

        const normalizedData = normalizeValue(data ?? []);
        setContractDetails((prevState) => ({
          ...prevState,
          [instrumentId]: normalizedData,
        }));
      } catch (err) {
        console.error('API error: ', err);
        setContractDetails({
          ...contractDetails,
          [instrumentId]: [],
        });
      }

      loadingContractDetails[instrumentId] = false;
      setContractDetailsLoading((prevLoading) => ({
        ...prevLoading,
        [instrumentId]: false,
      }));
    },
    [
      liquidNobleApi,
      setContractDetails,
      contractDetails,
      setContractDetailsLoading,
      instrumentId,
      contractDetailsLoading,
      skipSessionTypeCheck,
    ],
  );
  React.useEffect(() => {
    async function loadContractDetails() {
      if (
        !instrumentId ||
        instrumentId === CURRENCY_BSV ||
        (contractDetails && contractDetails[instrumentId] !== undefined)
      ) {
        return;
      }
      refetch();
    }
    loadContractDetails();
  }, [
    contractDetails,
    setContractDetails,
    liquidNobleApi,
    refetch,
    instrumentId,
  ]);
  return [
    contractDetails?.[instrumentId],
    refetch,
    contractDetailsLoading?.[instrumentId] ?? false,
  ];
};

export const useVisibleInstrumentContractDetails = () => {
  const liquidNobleApi = useLiquidNobleApi();

  const {
    contractDetails,
    setContractDetails,
    initialContractsLoading,
    setInitialContractsLoading,
  } = React.useContext(LockboxContext);

  const refetch = React.useCallback(
    async (force = false) => {
      if (!isLiquidNobleLoggedIn()) {
        return;
      }

      if (!force && (initialContractsLoading || contractsLoading)) {
        return;
      }

      contractsLoading = true;
      setInitialContractsLoading(true);

      try {
        const response = await liquidNobleApi.get(`/contracts/instruments/`);

        const { data } = response;
        Object.keys(data).forEach((instrumentId) => {
          const record = data[instrumentId];
          const normalizedData = normalizeValue(record);
          setContractDetails((prevState) => ({
            ...prevState,
            [instrumentId]: normalizedData,
          }));
        });
      } catch (err) {
        console.error('API error: ', err);
      }
    },
    [
      liquidNobleApi,
      setContractDetails,
      initialContractsLoading,
      setInitialContractsLoading,
    ],
  );
  React.useEffect(() => {
    async function loadContractDetails() {
      if (initialContractsLoading || contractsLoading) {
        return;
      }
      refetch();
    }
    loadContractDetails();
  }, [
    contractDetails,
    setContractDetails,
    liquidNobleApi,
    refetch,
    initialContractsLoading,
  ]);
  return [contractDetails, refetch];
};

export const useActivePersonalProfile = () => {
  const [profiles] = useLiquidNobleActiveProfiles();
  return profiles.find(
    (profile) => profile.individualEntityId === profile.entityId,
  );
};

export const useAnyProviderLoggedIn = () => {
  const isTokenizedLoggedIn = useIsLoggedIn();
  const isLqnLoggedIn = isLiquidNobleLoggedIn();
  return isLqnLoggedIn || isTokenizedLoggedIn;
};

export const useEntityTransactions = ({
  lockboxId,
  loadOnMount = true,
} = {}) => {
  const liquidNobleApi = useLiquidNobleApi();
  const {
    activeEntityId,
    transactionsLoading,
    setTransactionsLoading,
    transactionsData,
    setTransactionsData,
  } = React.useContext(EntityContext);
  const refetch = React.useCallback(
    async ({
      rowsPerPage,
      timestampFrom,
      timestampTo,
      preserveLoaded,
      onSuccess,
      onError,
    } = {}) => {
      if (transactionsLoading || !activeEntityId) {
        return;
      }
      setTransactionsLoading(true);
      try {
        const response = await liquidNobleApi.get(
          `/entity/${activeEntityId}/transactions/`,
          {
            params: {
              rows: rowsPerPage,
              timestamp_from: timestampFrom,
              timestamp_to: timestampTo,
              lockbox_id: lockboxId,
            },
          },
        );

        const { data } = response;
        const categories = data?.transactions ?? {};
        const pagination = data?.pagination ?? {};
        if (preserveLoaded && !!transactionsData) {
          const mergedData = {};
          Object.keys(categories).forEach((category) => {
            mergedData[category] = [
              ...(transactionsData[category] ?? []),
              ...categories[category],
            ];
          });
          mergedData.pagination = pagination;
          setTransactionsData(mergedData);
        } else {
          setTransactionsData({
            ...categories,
            pagination,
          });
        }
        onSuccess?.(categories);
      } catch (err) {
        console.error('API error: ', err);
        setTransactionsData([]);
        onError?.(err);
      }
      setTransactionsLoading(false);
    },
    [
      lockboxId,
      liquidNobleApi,
      transactionsData,
      setTransactionsData,
      setTransactionsLoading,
      transactionsLoading,
      activeEntityId,
    ],
  );

  React.useEffect(() => {
    async function loadTransactions() {
      refetch();
    }
    if (loadOnMount) {
      loadTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, loadOnMount]);
  return [transactionsData, refetch, transactionsLoading];
};

export const useEntityLockboxes = () => {
  const isLqn = isLiquidNobleLoggedIn();
  const liquidNobleApi = useLiquidNobleApi();
  const { profileId } = useActiveEntityIds();
  const tokenizedLockboxId = usePrimaryLockbox(profileId)?.id;

  const {
    activeEntityId,
    lockboxesLoading,
    setLockboxesLoading,
    lockboxesList,
    setLockboxesList,
  } = React.useContext(EntityContext);
  const refetch = React.useCallback(
    async ({ onSuccess, onError, force = false } = {}) => {
      if ((lockboxesLoading && !force) || !activeEntityId) {
        return;
      }
      setLockboxesLoading(true);
      if (!isLqn) {
        // Simulate a lockbox for Tokenized users
        if (tokenizedLockboxId) {
          setLockboxesList([
            {
              id: tokenizedLockboxId,
              name: 'Main',
              reference: '',
              is_primary: true,
              lockbox_type: LOCKBOX_TYPE_TRANSACTION,
            },
          ]);
          setLockboxesLoading(false);
        }
        return;
      }
      try {
        const response = await liquidNobleApi.get(
          `/entity/${activeEntityId}/lockboxes/`,
        );

        const { data } = response;
        const lockboxes = data?.lockboxes ?? [];
        setLockboxesList([...lockboxes]);
        onSuccess?.(lockboxes);
      } catch (err) {
        console.error('API error: ', err);
        setLockboxesList([]);
        onError?.(err);
      }
      setLockboxesLoading(false);
    },
    [
      liquidNobleApi,
      setLockboxesList,
      lockboxesLoading,
      setLockboxesLoading,
      activeEntityId,
      isLqn,
      tokenizedLockboxId,
    ],
  );

  React.useEffect(() => {
    async function loadTransactions() {
      refetch();
    }
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId]);

  React.useEffect(() => {
    if (isLqn) {
      return;
    }

    if (tokenizedLockboxId && lockboxesLoading && !lockboxesList?.length) {
      refetch({
        force: true,
      });
    }
  }, [tokenizedLockboxId, refetch, isLqn, lockboxesLoading, lockboxesList]);
  return [lockboxesList, refetch, lockboxesLoading];
};

export const useEntityOrders = (
  loadOnMount = false,
  planOrdersOnly = false,
) => {
  const liquidNobleApi = useLiquidNobleApi();
  const {
    activeEntityId,
    ordersLoading,
    setOrdersLoading,
    ordersData,
    setOrdersData,
    planOrdersData,
    setPlanOrdersData,
  } = React.useContext(EntityContext);
  const refetch = React.useCallback(
    async ({
      rowsPerPage,
      timestampFrom,
      timestampTo,
      preserveLoaded,
      onSuccess,
      onError,
    } = {}) => {
      if (ordersLoading || !activeEntityId) {
        return;
      }
      setOrdersLoading(true);
      timestampFrom = ensureUnixTimestamp(timestampFrom);
      timestampTo = ensureUnixTimestamp(timestampTo);
      try {
        const response = await liquidNobleApi.get(
          `/entity/${activeEntityId}/orders/`,
          {
            params: {
              rows: rowsPerPage,
              timestamp_from: timestampFrom,
              timestamp_to: timestampTo,
              plan_orders_only: planOrdersOnly ? 1 : 0,
            },
          },
        );

        const { data } = response;
        const orders = data?.orders ?? [];
        const pagination = data?.pagination ?? {};
        if (preserveLoaded && !!ordersData) {
          const mergedData = [...(ordersData.orders ?? []), ...orders];
          const uniqueMergedData = Array.from(
            new Map(mergedData.map((item) => [item.id, item])).values(),
          );
          if (planOrdersOnly) {
            setPlanOrdersData({
              orders: uniqueMergedData,
              pagination: pagination,
            });
          } else {
            setOrdersData({
              orders: uniqueMergedData,
              pagination: pagination,
            });
          }
        } else {
          if (planOrdersOnly) {
            setPlanOrdersData({
              orders,
              pagination,
            });
          } else {
            setOrdersData({
              orders,
              pagination,
            });
          }
        }
        onSuccess?.(orders);
      } catch (err) {
        // Ignore status 403 (restricted accounts)
        if (err?.status !== 403) {
          console.error(
            'Status ',
            err?.status,
            ': Entity orders API error: ',
            err,
          );
        }
        onError?.(err);
        if (planOrdersOnly) {
          setPlanOrdersData({});
        } else {
          setOrdersData({});
        }
      }
      setOrdersLoading(false);
    },
    [
      liquidNobleApi,
      ordersData,
      setOrdersData,
      setOrdersLoading,
      ordersLoading,
      activeEntityId,
      planOrdersOnly,
      setPlanOrdersData,
    ],
  );

  React.useEffect(() => {
    async function loadOrders() {
      refetch();
    }
    if (loadOnMount) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, loadOnMount, planOrdersOnly]);
  return [planOrdersOnly ? planOrdersData : ordersData, refetch, ordersLoading];
};

export const useEntityPortfolio = (loadOnMount = false) => {
  const liquidNobleApi = useLiquidNobleApi();
  const {
    activeEntityId,
    portfolioLoading,
    setPortfolioLoading,
    portfolioData,
    setPortfolioData,
  } = React.useContext(EntityContext);
  const refetch = React.useCallback(
    async ({
      instrumentId,
      timestampFrom,
      timestampTo,
      lockboxId,
      onSuccess,
      onError,
    } = {}) => {
      if (portfolioLoading || !activeEntityId) {
        return;
      }
      setPortfolioLoading(true);

      timestampFrom = ensureUnixTimestamp(timestampFrom);
      timestampTo = ensureUnixTimestamp(timestampTo);

      try {
        const response = await liquidNobleApi.get(
          `/entity/${activeEntityId}/portfolio/`,
          {
            params: {
              lockbox_id: lockboxId,
              instrument_id: instrumentId,
              timestamp_from: timestampFrom,
              timestamp_to: timestampTo,
            },
          },
        );

        const { data } = response;
        const portfolio = data?.portfolio ?? {};
        setPortfolioData(portfolio);
        onSuccess?.(portfolio);
      } catch (err) {
        // Ignore status 403 (restricted accounts)
        if (err?.status !== 403) {
          console.error('Status ', err?.status, ': API error: ', err);
        }
        onError?.(err);
        setPortfolioData({});
      }
      setPortfolioLoading(false);
    },
    [
      liquidNobleApi,
      setPortfolioData,
      setPortfolioLoading,
      portfolioLoading,
      activeEntityId,
    ],
  );

  React.useEffect(() => {
    async function loadPortfolio() {
      refetch();
    }
    if (loadOnMount) {
      loadPortfolio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, loadOnMount]);
  return [portfolioData, refetch, portfolioLoading];
};

export const useTokenNetworks = (tokenName, autoFetch = true) => {
  const liquidNobleApi = useLiquidNobleApi();
  const [networks, setNetworks] = React.useState(null);

  const loadTokenNetworks = React.useCallback(
    async (tokenName) => {
      const response = await liquidNobleApi.get(`/contracts/networks/`, {
        params: {
          token_name: tokenName,
        },
      });
      const { data } = response;
      if (data?.networks) {
        setNetworks(data?.networks);
      }
    },
    [liquidNobleApi, setNetworks],
  );

  React.useEffect(() => {
    if (autoFetch) {
      loadTokenNetworks(tokenName);
    }
  }, [loadTokenNetworks, autoFetch, tokenName]);
  return [networks, loadTokenNetworks];
};

export const useEntityWalletAddresses = (
  entityId,
  tokenName,
  autoFetch = true,
) => {
  const liquidNobleApi = useLiquidNobleApi();
  const { walletAddresses, setWalletAddresses } =
    React.useContext(EntityContext);

  const loadWalletAddresses = React.useCallback(
    async (tokenName, forceReload = false) => {
      if (!entityId) {
        return;
      }
      if (!isLiquidNobleLoggedIn()) {
        return;
      }
      if (walletAddresses[tokenName] && !forceReload) {
        return;
      }
      const response = await liquidNobleApi.get(
        `/user/${entityId}/entity/addresses/`,
        {
          params: {
            token_name: tokenName,
          },
        },
      );
      const { data } = response;
      if (data?.addresses) {
        setWalletAddresses({
          ...walletAddresses,
          [tokenName]: {
            addresses: data?.addresses,
            max: data?.max,
          },
        });
      }
    },
    [liquidNobleApi, entityId, setWalletAddresses, walletAddresses],
  );

  React.useEffect(() => {
    if (autoFetch) {
      loadWalletAddresses(tokenName);
    }
  }, [loadWalletAddresses, autoFetch, tokenName]);
  return [walletAddresses, loadWalletAddresses];
};

export const useEntityNewWalletAddress = (entityId) => {
  const liquidNobleApi = useLiquidNobleApi();

  const requestWalletAddress = React.useCallback(
    async (tokenName, network) => {
      if (!entityId) {
        console.log('requestWalletAddress failed: No entity ID provided');
        return;
      }
      const unlocking_key = getSessionUnlockingKey();
      try {
        const response = await liquidNobleApi.post(
          `/user/${entityId}/entity/addresses/new/`,
          {
            unlocking_key,
            token_name: tokenName,
            wallet_network: network,
          },
        );
        const { data } = response;
        return data?.address;
      } catch (err) {
        errorResponseNotify(err);
        return null;
      }
    },
    [liquidNobleApi, entityId],
  );

  return requestWalletAddress;
};
export const useEntityRefreshWalletBalance = (entityId) => {
  const liquidNobleApi = useLiquidNobleApi();

  const requestWalletBalance = React.useCallback(async () => {
    if (!entityId) {
      console.log('requestWalletBalance failed: No entity ID provided');
      return;
    }
    try {
      const response = await liquidNobleApi.post(
        `/user/${entityId}/entity/addresses/refresh-balance/`,
        {},
      );
      const { data } = response;
      return data?.status === 'success';
    } catch (err) {
      errorResponseNotify(err);
      return null;
    }
  }, [liquidNobleApi, entityId]);

  return requestWalletBalance;
};

export const usePostLogout = () => {
  // This hook is used to clear out all the context data when the user logs out

  const { setLockboxBalances, setLockboxLoading } =
    React.useContext(LockboxContext);
  const { setEntityInfo, setActiveLockboxId, setActiveProfiles } =
    React.useContext(EntityContext);
  const { setInboxMessages } = React.useContext(InboxContext);

  return React.useCallback(() => {
    setLockboxBalances({});
    setLockboxLoading({});
    setEntityInfo({});
    setInboxMessages(null);
    setActiveLockboxId(null);
    setActiveProfiles(null);
  }, [
    setLockboxBalances,
    setEntityInfo,
    setInboxMessages,
    setLockboxLoading,
    setActiveLockboxId,
    setActiveProfiles,
  ]);
};

export const useLogout = () => {
  const history = useHistory();
  const onPostLogout = usePostLogout();
  return React.useCallback(
    (queryParams = '') => {
      window.localStorage.removeItem('__BRANCH__');
      window.sessionStorage.removeItem('bankingSuggested');
      window.sessionStorage.removeItem('authToken');
      window.sessionStorage.removeItem('verificationFormValues');
      saveSessionUsername(null);
      saveAuthToken(null);
      onPostLogout();
      clearSessionMetadata();

      history.push(`/log-in?${queryParams}`);
      location.reload();
    },
    [onPostLogout, history],
  );
};

export const ensureUnixTimestamp = (timestamp, apiCompatible = true) => {
  const mod = apiCompatible ? 1000 : 1;
  if (timestamp && typeof timestamp !== 'number') {
    timestamp = new Date(timestamp).getTime() / mod;
  } else if (timestamp) {
    timestamp = Math.floor(timestamp / mod);
  }
  return timestamp;
};

export const isLqnHandle = (handle) => {
  return handle?.includes('@lqn.app');
};

/**
 * Hook to fetch and manage user's accumulation plans
 * Usage: const [plansData, onRefetchPlans, plansLoading] = useEntityPlans(false);
 * For testing: const [plansData, onRefetchPlans, plansLoading] = useEntityPlans(false, true);
 */
export const useEntityPlans = (loadOnMount = false) => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);
  const [plansLoading, setPlansLoading] = React.useState(false);
  const [plansData, setPlansData] = React.useState(null);

  const refetch = React.useCallback(
    async ({ onSuccess, onError } = {}) => {
      if (plansLoading || !activeEntityId) {
        return;
      }
      setPlansLoading(true);

      try {
        const response = await liquidNobleApi.get('/order/plan/list/', {
          params: {
            entity_id: activeEntityId,
          },
        });

        const { data } = response;
        const plans = {
          plans: data?.plans ?? [],
        };
        setPlansData(plans);
        onSuccess?.(plans);
      } catch (err) {
        // Ignore status 403 (restricted accounts)
        if (err?.status !== 403) {
          console.error(
            'Status ',
            err?.status,
            ': Entity plans API error: ',
            err,
          );
        }
        onError?.(err);
        setPlansData({ plans: [] });
      }
      setPlansLoading(false);
    },
    [liquidNobleApi, plansLoading, activeEntityId],
  );

  React.useEffect(() => {
    async function loadPlans() {
      refetch();
    }
    if (loadOnMount) {
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, loadOnMount]);

  return [plansData, refetch, plansLoading];
};

export const useFetchOrderMinimum = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();

  return React.useCallback(
    async (instrument_id, order_type, display_currency_code) => {
      if (!instrument_id || !entityId) {
        return null;
      }
      try {
        const response = await liquidNobleApi.get('/order/minimum/', {
          params: {
            entity_id: entityId,
            instrument_id,
            order_type,
            display_currency_code,
          },
        });
        const { data } = response;
        return data?.minimum_order_amount;
      } catch (error) {
        errorResponseNotify(error);
        return null;
      }
    },
    [liquidNobleApi, entityId],
  );
};

/**
 * Hook to submit a request to create a new accumulation plan
 * Usage: const onSubmitCreatePlan = useSubmitCreatePlan();
 *
 * Call with:
 * await onSubmitCreatePlan({
 *   profile_id,
 *   instrument_id,
 *   order_quantity_grams, // OR order_amount_fiat + order_amount_currency
 *   time_rule_type,
 *   frequency,
 *   // ... other fields
 *   mfa_code,
 *   unlocking_key,
 *   passkey_assertion,
 *   onSuccess: (data) => {},
 *   onError: (error) => {},
 * });
 */
export const useSubmitCreatePlan = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();

  return async ({
    profile_id,
    instrument_id,
    order_quantity_grams,
    order_amount_fiat,
    order_amount_currency,
    time_rule_type,
    deposit_percentage,
    frequency,
    day_of_week,
    day_of_month,
    custom_dates,
    execution_time,
    price_unit,
    price_min,
    price_max,
    price_fiat,
    mfa_code,
    unlocking_key,
    passkey_assertion,
    onSuccess,
    onError,
  }) => {
    const postData = {
      entity_id: entityId,
      profile_id,
      instrument_id,
      order_quantity_grams,
      order_amount_fiat,
      order_amount_currency,
      time_rule_type,
      deposit_percentage,
      frequency,
      day_of_week,
      day_of_month,
      custom_dates,
      execution_time,
      price_unit,
      price_min,
      price_max,
      price_fiat,
      mfa_code,
      unlocking_key,
      passkey_assertion,
    };

    try {
      const response = await liquidNobleApi.post(
        '/order/plan/create/',
        postData,
      );
      const { data } = response;

      // Handle MFA requirement
      if (data?.status === 'mfa_required') {
        onSuccess?.(data);
        return;
      }

      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

/**
 * Hook to toggle an accumulation plan's paused state
 * Usage: const onPausePlan = usePausePlan();
 */
export const usePausePlan = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();

  return async ({ plan_id, pause, onSuccess, onError }) => {
    try {
      const response = await liquidNobleApi.post(`/order/plan/pause/`, {
        entity_id: entityId,
        plan_id,
        pause,
      });
      const { data } = response;
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

/**
 * Hook to delete an accumulation plan
 * Usage: const onDeletePlan = useCancelPlan();
 */
export const useCancelPlan = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();

  return async ({
    plan_id,
    mfa_code,
    passkey_assertion,
    onSuccess,
    onError,
  }) => {
    try {
      const response = await liquidNobleApi.post(`/order/plan/cancel/`, {
        entity_id: entityId,
        plan_id,
        mfa_code,
        passkey_assertion,
      });
      const { data } = response;
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

/**
 * Hook to fetch the list of gift cards issued by the active entity.
 * Usage: const [giftCardsData, onRefetchGiftCards, giftCardsLoading] = useGiftCards(false);
 */
export const useGiftCards = (loadOnMount = false) => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);
  const [giftCardsLoading, setGiftCardsLoading] = React.useState(false);
  const [giftCardsData, setGiftCardsData] = React.useState(null);

  const refetch = React.useCallback(
    async ({ onSuccess, onError } = {}) => {
      if (giftCardsLoading || !activeEntityId) {
        return;
      }
      setGiftCardsLoading(true);
      try {
        const response = await liquidNobleApi.get('/gift-card/list/', {
          params: { entity_id: activeEntityId },
        });
        const cards = { cards: response.data?.cards ?? [] };
        setGiftCardsData(cards);
        onSuccess?.(cards);
      } catch (err) {
        if (err?.status !== 403) {
          console.error('Gift cards API error:', err);
        }
        onError?.(err);
        setGiftCardsData({ cards: [] });
      }
      setGiftCardsLoading(false);
    },
    [liquidNobleApi, giftCardsLoading, activeEntityId],
  );

  React.useEffect(() => {
    if (loadOnMount) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, loadOnMount]);

  return [giftCardsData, refetch, giftCardsLoading];
};

/**
 * Hook to fetch details (including base64 card image) for a single gift card.
 * Usage: const onFetchGiftCardDetails = useGiftCardDetails();
 * Call: await onFetchGiftCardDetails({ card_id, onSuccess, onError });
 */
export const useGiftCardDetails = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);

  return React.useCallback(
    async ({ card_id, onSuccess, onError }) => {
      try {
        const response = await liquidNobleApi.get('/gift-card/details/', {
          params: { entity_id: activeEntityId, card_id },
        });
        onSuccess?.(response.data);
      } catch (err) {
        console.error('Gift card details API error:', err);
        onError?.(err);
      }
    },
    [liquidNobleApi, activeEntityId],
  );
};

export const useGiftCardPreview = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);

  return React.useCallback(
    async ({ quote_id, design_id, message, onSuccess, onError }) => {
      try {
        const response = await liquidNobleApi.get('/gift-card/preview/', {
          params: { entity_id: activeEntityId, quote_id, design_id, message },
        });
        onSuccess?.(response.data);
        return response.data;
      } catch (err) {
        console.error('Gift card preview API error:', err);
        onError?.(err);
      }
    },
    [liquidNobleApi, activeEntityId],
  );
};

export const useCheckGiftCardCode = () => {
  const liquidNobleApi = useLiquidNobleApi();

  return React.useCallback(
    async ({ code, reCaptchaToken }) => {
      try {
        const response = await liquidNobleApi.post('/gift-card/check/', {
          code,
          token: reCaptchaToken,
        });
        return response.data;
      } catch (err) {
        console.error('Gift card preview API error:', err);
      }
    },
    [liquidNobleApi],
  );
};

/**
 * Hook to fetch available gift card design templates.
 * Usage: const [designsData, onRefetchDesigns, designsLoading] = useGiftCardDesigns();
 */
export const useGiftCardDesigns = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const [designsLoading, setDesignsLoading] = React.useState(false);
  const [designsData, setDesignsData] = React.useState(null);

  const refetch = React.useCallback(
    async ({ onSuccess, onError } = {}) => {
      if (designsLoading) return;
      setDesignsLoading(true);
      try {
        const response = await liquidNobleApi.get('/gift-card/designs/');
        const designs = { designs: response.data?.designs ?? [] };
        setDesignsData(designs);
        onSuccess?.(designs);
      } catch (err) {
        console.error('Gift card designs API error:', err);
        onError?.(err);
        setDesignsData({ designs: [] });
      }
      setDesignsLoading(false);
    },
    [liquidNobleApi, designsLoading],
  );

  React.useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi]);

  return [designsData, refetch, designsLoading];
};

export const useCreateGiftCard = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();
  const { getPasskeyAssertion } = usePasskey();

  return async ({
    profile_id,
    instrument_id,
    quote_id,
    design_id,
    custom_message,
    recipient_handle,
    recipient_email,
    recipient_name,
    notify_recipient,
    mfa_code,
    onSuccess,
    onError,
    ...rest
  }) => {
    const passkey_assertion = await getPasskeyAssertion();
    const unlocking_key = getSessionUnlockingKey();
    const postData = {
      entity_id: entityId,
      profile_id,
      instrument_id,
      quote_id,
      design_id,
      custom_message,
      recipient_handle,
      recipient_email,
      recipient_name,
      notify_recipient,
      mfa_code,
      passkey_assertion,
      unlocking_key,
      ...rest,
    };
    try {
      const response = await liquidNobleApi.post('/order/gift-card/', postData);
      const { data } = response;
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

export const useGiftCardRedeem = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { entityId } = useActiveEntityIds();
  const { getPasskeyAssertion } = usePasskey();

  return async ({ code, mfa_code, onSuccess, onError, ...rest }) => {
    const passkey_assertion = await getPasskeyAssertion();
    const unlocking_key = getSessionUnlockingKey();
    const postData = {
      entity_id: entityId,
      code,
      mfa_code,
      passkey_assertion,
      unlocking_key,
      ...rest,
    };
    try {
      const response = await liquidNobleApi.post(
        '/gift-card/redeem/',
        postData,
      );
      const { data } = response;
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

export const useGiftCardRedeemConfirm = () => {
  const liquidNobleApi = useLiquidNobleApi();
  return async ({ confirmationCode, onSuccess, onError, ...rest }) => {
    const postData = {
      code: confirmationCode,
      ...rest,
    };
    try {
      const response = await liquidNobleApi.post(
        '/gift-card/redeem/confirm/',
        postData,
      );
      const { data } = response;
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};
